import { Link } from 'react-router-dom';
import { Heart, Bookmark, Building2, MessageSquare, ArrowRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/store/useAuth';
import { useFavorites } from '@/store/useFavorites';
import { ListingCard } from '@/components/common/ListingCard';
import { getListingsByIds } from '@/services/api';
import { getSavedSearches, getConversations } from '@/services/consumerApi';

export default function Overview() {
  const { user } = useAuth();
  const mlsToItem = useFavorites((s) => s.mlsToItem);
  const favIds = Object.keys(mlsToItem);

  const { data: searchesData } = useQuery({
    queryKey: ['saved-searches'],
    queryFn: getSavedSearches,
  });

  const { data: convsData } = useQuery({
    queryKey: ['conversations'],
    queryFn: getConversations,
    refetchInterval: 30000,
  });

  const { data: recent = [] } = useQuery({
    queryKey: ['recent-listings', favIds.slice(0, 3).join(',')],
    queryFn: () => getListingsByIds(favIds.slice(0, 3)),
    enabled: favIds.length > 0,
  });

  const savedSearchCount = searchesData?.results?.length ?? 0;
  const unreadMessages = (convsData?.results || []).reduce((a, c) => a + (c.unread_count || 0), 0);

  const tiles = [
    { label: 'Saved homes', value: favIds.length, icon: Heart, to: '/dashboard/saved-homes', color: 'text-rose-500 bg-rose-50' },
    { label: 'Saved searches', value: savedSearchCount, icon: Bookmark, to: '/dashboard/saved-searches', color: 'text-blue-600 bg-blue-50' },
    { label: 'My listings', value: 0, icon: Building2, to: '/dashboard/my-listings', color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Messages', value: unreadMessages, icon: MessageSquare, to: '/dashboard/messages', color: 'text-amber-600 bg-amber-50' },
  ];

  return (
    <div>
      <div className="rounded-2xl bg-blue-700 p-6 text-white shadow-sm">
        <h1 className="font-display text-2xl font-bold">Welcome back, {user?.name?.split(' ')[0]} </h1>
        <p className="mt-1 text-white/85">Here&rsquo;s what&rsquo;s happening with your home search.</p>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {tiles.map((t) => (
          <Link key={t.label} to={t.to} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md" data-testid={`stat-tile-${t.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className={`grid h-10 w-10 place-items-center rounded-xl ${t.color}`}><t.icon size={20} /></div>
            <p className="mt-3 font-display text-2xl font-bold text-slate-900">{t.value}</p>
            <p className="text-sm text-slate-500">{t.label}</p>
          </Link>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Recently saved</h2>
        <Link to="/dashboard/saved-homes" className="inline-flex items-center gap-1 text-sm font-medium text-blue-600">View all <ArrowRight size={15} /></Link>
      </div>
      {recent.length ? (
        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {recent.map((l) => <ListingCard key={l.id} listing={l} />)}
        </div>
      ) : (
        <p className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
          No saved homes yet. <Link to="/listings" className="font-medium text-blue-600">Start browsing →</Link>
        </p>
      )}
    </div>
  );
}
